import React from 'react'
import {View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useNavigation} from '@react-navigation/native'
import {type NativeStackScreenProps} from '@react-navigation/native-stack'

import {
  type CommonNavigatorParams,
  type NavigationProp,
} from '#/lib/routes/types'
import {usePremium} from '#/state/premium'
import * as SettingsList from '#/screens/Settings/components/SettingsList'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import {Phone_Stroke2_Corner0_Rounded as PhoneIcon} from '#/components/icons/Phone'
import {Play_Stroke2_Corner0_Rounded as PlayIcon} from '#/components/icons/Play'
import {VideoCamera_Stroke2_Corner0_Rounded as VideoCameraIcon} from '#/components/icons/VideoCamera'
import * as Layout from '#/components/Layout'
import {Text} from '#/components/Typography'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'BateskySettings'>

export function BateskyMembershipSettingsScreen({}: Props) {
  const {_} = useLingui()
  const t = useTheme()
  const navigation = useNavigation<NavigationProp>()
  const {
    isPremium,
    expiresAt,
    saleId,
    isTrial,
    isLoading: isPremiumLoading,
  } = usePremium()

  const handleJoinPremium = React.useCallback(() => {
    navigation.navigate('Premium')
  }, [navigation])

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return ''
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  return (
    <Layout.Screen>
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Batesky Membership</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>
      <Layout.Content>
        <SettingsList.Container>
          <View style={[a.p_xl, a.gap_lg]}>
            <View style={[a.flex_row, a.align_center, a.gap_sm]}>
              <Text style={[a.text_md, t.atoms.text]}>
                <Trans>Status:</Trans>
              </Text>
              {isPremium ? (
                <View
                  style={[
                    a.px_sm,
                    a.py_xs,
                    a.rounded_sm,
                    {backgroundColor: t.palette.positive_600},
                  ]}>
                  <Text style={[a.text_sm, a.font_bold, {color: '#000'}]}>
                    {isTrial ? (
                      <Trans>Premium Trial</Trans>
                    ) : (
                      <Trans>Active</Trans>
                    )}
                  </Text>
                </View>
              ) : (
                <Text style={[a.text_md, t.atoms.text_contrast_medium]}>
                  <Trans>Not subscribed</Trans>
                </Text>
              )}
            </View>

            {isPremium && expiresAt && (
              <View style={[a.gap_xs]}>
                <Text style={[a.text_md, t.atoms.text]}>
                  <Trans>Expires:</Trans> {formatDate(expiresAt)}
                </Text>
              </View>
            )}

            {isPremium && saleId && (
              <View style={[a.gap_xs]}>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>Subscription ID:</Trans> {saleId}
                </Text>
              </View>
            )}

            <View style={[a.gap_md, a.mt_md]}>
              <Text style={[a.text_md, t.atoms.text]}>
                <Trans>With Premium you have access to:</Trans>
              </Text>
              <View style={[a.gap_sm, a.pl_sm]}>
                <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                  <PhoneIcon size="sm" style={t.atoms.text} />
                  <Text style={[a.text_md, t.atoms.text]}>
                    <Trans>Rooms</Trans>
                  </Text>
                </View>
                <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                  <VideoCameraIcon size="sm" style={t.atoms.text} />
                  <Text style={[a.text_md, t.atoms.text]}>
                    <Trans>Videos</Trans>
                  </Text>
                </View>
                <View style={[a.flex_row, a.align_center, a.gap_sm]}>
                  <PlayIcon size="sm" style={t.atoms.text} />
                  <Text style={[a.text_md, t.atoms.text]}>
                    <Trans>Batefeed</Trans>
                  </Text>
                </View>
              </View>

              {!isPremium && !isPremiumLoading && (
                <Button
                  label={_(msg`Join Premium`)}
                  onPress={handleJoinPremium}
                  size="large"
                  variant="solid"
                  color="primary"
                  style={[a.rounded_md]}>
                  <ButtonText>
                    <Trans>Join Premium</Trans>
                  </ButtonText>
                </Button>
              )}
            </View>

            {isPremium && (
              <View style={[a.gap_md, a.mt_md]}>
                <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                  <Trans>
                    To cancel your subscription, please contact support.
                  </Trans>
                </Text>
              </View>
            )}
          </View>
        </SettingsList.Container>
      </Layout.Content>
    </Layout.Screen>
  )
}
